<script setup lang="ts">
import type { Task, TaskUser } from '~/models/AssignTask'
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import { faEye, faLock } from '@fortawesome/duotone-regular-svg-icons'
import { PendoFeature } from '~/models/Pendo'
import { TaskStatus } from '~/models/AssignTask'
import { useAssignTask } from '~/composables/useAssignTask'
import Button from '~/components/ui/Button.vue'
import AuDialog from '~/components/ui/AuDialog.vue'

const { student, task } = defineProps<{ student: TaskUser, task: Task }>()

const { t } = useI18n()
const { updateTaskUser } = useAssignTask()

const confirmDialogOpen = ref(false)

const statusIsOpen = computed(() => student.status === TaskStatus.Open)
const buttonText = computed(() => {
  if (updateTaskUser.isPending.value) {
    return !statusIsOpen.value ? t('assign.openingExam') : t('assign.closingExam')
  }
  return statusIsOpen.value ? t('assign.closeTheExam') : t('assign.openTheExam')
})

const updateStudent = async () => {
  await updateTaskUser.mutateAsync({
    task,
    user: student,
    status: statusIsOpen.value ? TaskStatus.Closed : TaskStatus.Open
  })
  confirmDialogOpen.value = false
}

const tracking = computed(() => ({
  'data-pendo': statusIsOpen.value
    ? PendoFeature.TermExam.CloseStudent
    : PendoFeature.TermExam.OpenStudent
}))
</script>

<template>
  <li class="border-green-10 relative flex size-full items-center justify-between border-b px-1 py-2 font-medium last:border-b-0">
    <div class="flex items-center gap-4">
      <div
        class="flex items-center justify-center rounded-lg p-1.5 text-base transition-all"
        :class="statusIsOpen ? 'bg-green-10 text-green-50' : 'bg-coral-20 text-coral-50'"
      >
        <font-awesome-icon
          :icon="statusIsOpen ? faEye : faLock"
          :class="statusIsOpen ? 'text-green-50' : 'text-coral-50'"
        />
      </div>
      <span v-text="student.fullName" />
    </div>
    <AuDialog
      :open="confirmDialogOpen"
      size="sm"
      :title="t('assign.confirm')"
      @close="confirmDialogOpen = false"
    >
      <template #trigger>
        <Button
          size="sm"
          variant="outline"
          @click="confirmDialogOpen = true"
        >
          {{ statusIsOpen ? t('assign.close') : t('assign.open') }}
        </Button>
      </template>
      <template #content>
        <p
          class="font-medium"
          v-text="t('assign.confirmInfo', {
            status: (statusIsOpen ? t('assign.confirmClose') : t('assign.open')).toLowerCase(),
            student: student.fullName,
          })"
        />
        <div class="mt-4 flex grid grid-cols-2 gap-4">
          <Button
            variant="outline"
            size="lg"
            center
            @click="confirmDialogOpen = false"
          >
            {{ t('cancel') }}
          </Button>
          <Button
            :color="statusIsOpen ? 'coral' : 'green'"
            size="lg"
            :disabled="updateTaskUser.isPending.value"
            center
            v-bind="tracking"
            @click="updateStudent"
          >
            {{ buttonText }}
          </Button>
        </div>
      </template>
    </AuDialog>
  </li>
</template>
